/**
 * Purchase-flow loader: a ticket being punched ("composté").
 *
 * Replaces the generic spinner on the wizard's waiting states so the loading
 * phase tells the story of what is happening: the checkout creation, the
 * payment return verification and the ticket issuance all show a ticket
 * getting validated. Decorative — the surrounding text carries the state.
 */

import { motion } from "motion/react";
import React, { useId } from "react";
import { useReducedMotion } from "../../hooks/useReducedMotion.hook";
import { getUIColorPalette } from "../../theme/ColorPalette";

/** Props for {@link TicketPurchaseLoader}. */
export interface TicketPurchaseLoaderProps {

    /** Dark UI palette toggle (same convention as the wizard steps). */
    "isDarkUi"?: boolean;

    /** Rendered width in pixels (height follows the ticket ratio). */
    "size"?: number;

}

/** Duration of one punch cycle; shared by the dot, ripple and ticket shake. */
const CYCLE_SECONDS = 1.6;

/**
 * Renders the animated ticket-punch loader.
 *
 * @param props - Component props.
 * @returns The loader SVG.
 */
export const TicketPurchaseLoader: React.FC<TicketPurchaseLoaderProps> = ( { isDarkUi = false, size = 64 } ) => {
    const palette = getUIColorPalette( isDarkUi );
    const reducedMotion = useReducedMotion();
    const maskId = useId();

    return (
        <svg
            aria-hidden="true"
            height={size * ( 64 / 96 )}
            viewBox="0 0 96 64"
            width={size}
        >
            <defs>
                <mask id={maskId}>
                    <rect
                        fill="#ffffff"
                        height="36"
                        rx="5"
                        width="80"
                        x="8"
                        y="14"
                    />
                    <circle
                        cx="8"
                        cy="32"
                        fill="#000000"
                        r="6"
                    />
                    <circle
                        cx="88"
                        cy="32"
                        fill="#000000"
                        r="6"
                    />
                </mask>
            </defs>
            <motion.g
                animate={reducedMotion
                    ? undefined
                    : {
                        "rotate": [ 0, 0, -1.2, 0.6, 0 ],
                        "y":      [ 0, 0, 1.5, 0, 0 ],
                    }}
                style={{
                    "transformBox":    "fill-box",
                    "transformOrigin": "center",
                }}
                transition={{
                    "duration": CYCLE_SECONDS,
                    "repeat":   Infinity,
                    "times":    [ 0, 0.32, 0.38, 0.46, 1 ],
                }}
            >
                <rect
                    fill={palette.overlayCardBackgroundSoft}
                    height="36"
                    mask={`url(#${ maskId })`}
                    rx="5"
                    stroke={palette.overlayBorderStrong}
                    strokeWidth="2.5"
                    width="80"
                    x="8"
                    y="14"
                />
                <line
                    stroke={palette.overlayTextSecondary}
                    strokeDasharray="2.5 3"
                    strokeWidth="1.5"
                    x1="68"
                    x2="68"
                    y1="19"
                    y2="45"
                />
                <rect
                    fill={palette.overlayTextSecondary}
                    height="4"
                    rx="2"
                    width="24"
                    x="16"
                    y="23"
                />
                <rect
                    fill={palette.overlayTextSecondary}
                    height="4"
                    opacity="0.55"
                    rx="2"
                    width="18"
                    x="16"
                    y="33"
                />
            </motion.g>
            <motion.circle
                animate={reducedMotion
                    ? undefined
                    : {
                        "opacity": [ 0, 0.55, 0, 0 ],
                        "r":       [ 5, 5, 16, 16 ],
                    }}
                cx="48"
                cy="32"
                fill="none"
                stroke={palette.brandPrimary}
                strokeWidth="2"
                transition={{
                    "duration": CYCLE_SECONDS,
                    "repeat":   Infinity,
                    "times":    [ 0, 0.32, 0.6, 1 ],
                }}
            />
            <motion.circle
                animate={reducedMotion
                    ? undefined
                    : {
                        "opacity": [ 0, 1, 1, 0 ],
                        "r":       [ 14, 5, 5, 5 ],
                    }}
                cx="48"
                cy="32"
                fill={palette.brandPrimary}
                transition={{
                    "duration": CYCLE_SECONDS,
                    "repeat":   Infinity,
                    "times":    [ 0, 0.32, 0.8, 1 ],
                }}
            />
            {reducedMotion && (
                <circle
                    cx="48"
                    cy="32"
                    fill={palette.brandPrimary}
                    r="5"
                />
            )}
        </svg>
    );
};
